/**
 * Unity ID-based Records C# Template Renderer
 * Creates an IRecord interface, sealed record classes per blueprint, and a Db class with typed retrieval
 */

import { ExportTemplate, NamingConvention, TemplateField } from '@/types/exportTemplates';
import type { StringsItem } from '@/lib/stores/stringsStore';
import { applyNamingConvention } from '../../core/naming';
import { extractFields } from '../../fieldMapping/extraction';
import { LookupContext } from '../../fieldMapping/references';

/**
 * Render ID-based records pattern for Unity
 * Creates an IRecord interface, sealed record classes per blueprint, and a Db class with typed retrieval
 * 
 * @param items - Items to export
 * @param template - Export template configuration
 * @param blueprints - Available blueprints
 * @param folderPathResolver - Function to resolve folder IDs to paths
 * @param types - Optional types data for reference resolution
 * @param tags - Optional tags data for reference resolution
 * @param allItemsForLookup - Optional all items for glossaryItem lookups
 * @param namingConventionOverride - Optional override for instance naming
 * @param customNamespace - Optional custom namespace (default: 'Grimoire.Data')
 * @param customClassName - Optional custom class name (default: 'Db')
 * @param stringsItems - Optional translation strings data
 * @param dialogs - Optional dialogs array
 * @param documents - Optional documents array for document field resolution
 */
export function renderIdBasedRecordsCSharpTemplate(
  items: any[],
  template: ExportTemplate,
  blueprints: any[],
  folderPathResolver: (folderId: string | null | undefined) => string,
  types?: any[],
  tags?: any[],
  allItemsForLookup?: any[],
  namingConventionOverride?: NamingConvention,
  customNamespace?: string,
  customClassName?: string,
  stringsItems?: StringsItem[],
  dialogs?: any[],
  documents?: any[],
  gameLanguages?: string[]
): string {
  const lookupContext: LookupContext = {
    types,
    tags,
    allItems: allItemsForLookup || items,
    dialogs: dialogs,
    documents: documents
  };

  // Use custom namespace and class name or defaults
  const namespace = customNamespace || 'Grimoire.Data';
  const className = customClassName || 'Db';

  // For ID-based records, always use the template's built-in conventions for class and field names
  // The naming override only affects IDs (instanceName convention)
  const fieldConvention = template.namingConventions.fieldName;
  const templateConvention = template.namingConventions.templateName;
  const idConvention = namingConventionOverride || template.namingConventions.instanceName;

  // Group items by blueprint to create record classes
  const blueprintGroups = new Map<string, { blueprint: any, items: any[] }>();

  items.forEach(item => {
    const blueprintId = item.blueprint_id;
    if (!blueprintId) return;

    if (!blueprintGroups.has(blueprintId)) {
      const blueprint = blueprints.find(bp => bp.id === blueprintId);
      if (blueprint) {
        blueprintGroups.set(blueprintId, { blueprint, items: [] });
      }
    }

    const group = blueprintGroups.get(blueprintId);
    if (group) {
      group.items.push(item);
    }
  });

  // When only one language is configured, suppress translation keys and export as plain strings
  const useTranslationKeys = (gameLanguages?.length || 1) > 1;
  
  // Create a map of string_id to StringsItem for quick lookup
  // When single-language mode, keep stringsMap empty so translation lookups fall through to plain strings
  const stringsMap = new Map<string, StringsItem>();
  if (stringsItems && useTranslationKeys) {
    stringsItems.forEach(stringItem => {
      stringsMap.set(stringItem.id, stringItem);
    });
  }

  // Helper to generate stable ID from item name and folder path
  const generateItemId = (item: any): string => {
    const folderPath = folderPathResolver(item.folder);
    const itemName = applyNamingConvention(item.name || 'unnamed', idConvention);
    
    if (folderPath) {
      // Split folder path and get the immediate parent folder (last segment)
      const folderSegments = folderPath.split('/').filter(f => f);
      const parentFolder = folderSegments.length > 0 
        ? applyNamingConvention(folderSegments[folderSegments.length - 1], idConvention)
        : '';
      
      return parentFolder ? `${itemName}_${parentFolder}` : itemName;
    }
    
    return itemName;
  };

  // Helper to get C# type from mapped type string AND actual value
  const getCSharpType = (mappedType: string, actualValue?: any, isTranslatable: boolean = false, field?: TemplateField): string => {
    // Check if this is a reference field
    if (field?.isReference && field?.referenceType) {
      if (field.referenceType === 'types') {
        return Array.isArray(actualValue) ? 'TypeElement[]' : 'TypeElement';
      }
      if (field.referenceType === 'tags') {
        return Array.isArray(actualValue) ? 'TagElement[]' : 'TagElement';
      }
      if (field.referenceType === 'dialog') {
        // Dialog references store dialog keys as strings
        return Array.isArray(actualValue) ? 'string[]' : 'string';
      }
    }
    
    // If translatable, use TranslationKey instead of string
    if (isTranslatable) {
      if (Array.isArray(actualValue)) return 'TranslationKey[]';
      return 'TranslationKey';
    }
    
    if (Array.isArray(actualValue)) return 'string[]';
    if (mappedType.includes('bool')) return 'bool';
    if (mappedType.includes('int')) {
      // If the actual value has decimals, use double instead of int
      if (typeof actualValue === 'number' && !Number.isInteger(actualValue)) return 'double';
      return 'int';
    }
    if (mappedType.includes('float')) return 'float';
    if (mappedType.includes('double')) return 'double';
    if (mappedType.includes('[]') || mappedType.includes('Array')) return 'string[]';
    return 'string';
  };

  // Helper to format value for C#
  const formatValueCSharp = (value: any, mappedType: string, field?: TemplateField): string => {
    // Handle reference fields - generate object references
    if (field?.isReference && field?.referenceData && field?.referenceType) {
      console.log(`🎯 formatValueCSharp: Reference field detected - type=${field.referenceType}`);
      
      if (field.referenceType === 'types') {
        if (Array.isArray(field.referenceData)) {
          const references = field.referenceData
            .map(refObj => {
              const parentTypeName = refObj._parentTypeName || 'UnknownType';
              const elementName = refObj.name || refObj.id || 'Unknown';
              const safeTypeName = parentTypeName.replace(/[^a-zA-Z0-9_]/g, '_');
              const safeElementName = elementName.replace(/[^a-zA-Z0-9_]/g, '_');
              return `Variables.${safeTypeName}.${safeElementName}`;
            })
            .join(', ');
          console.log(`  ✅ Generated array of Variable references: new VariableElement[] { ${references} }`);
          return `new VariableElement[] { ${references} }`;
        } else {
          const parentTypeName = field.referenceData._parentTypeName || 'UnknownType';
          const elementName = field.referenceData.name || field.referenceData.id || 'Unknown';
          const safeTypeName = parentTypeName.replace(/[^a-zA-Z0-9_]/g, '_');
          const safeElementName = elementName.replace(/[^a-zA-Z0-9_]/g, '_');
          const suffix = (mappedType === 'string') ? '.Name' : '';
          const variableReference = `Variables.${safeTypeName}.${safeElementName}${suffix}`;
          console.log(`  ✅ Generated Variable reference: ${variableReference}`);
          return variableReference;
        }
      } else if (field.referenceType === 'tags') {
        if (Array.isArray(field.referenceData)) {
          const values = field.referenceData
            .map(refObj => {
              const val = refObj.value || refObj.code || refObj.name || refObj.id;
              return `"${String(val).replace(/"/g, '\\"')}"`;
            })
            .join(', ');
          console.log(`  ✅ Generated array of tag values: new[] { ${values} }`);
          return `new[] { ${values} }`;
        } else {
          const val = field.referenceData.value || field.referenceData.code || field.referenceData.name || field.referenceData.id;
          const stringValue = `"${String(val).replace(/"/g, '\\"')}"`;
          console.log(`  ✅ Generated tag value: ${stringValue}`);
          return stringValue;
        }
      } else if (field.referenceType === 'glossaryItem') {
        const itemName = field.referenceData.name || field.referenceData.id || 'Unknown';
        const safeItemName = itemName.replace(/[^a-zA-Z0-9_]/g, '_');
        console.log(`  ⚠️ GlossaryItem reference: ${safeItemName} - using string for now`);
        return `"${safeItemName}" /* TODO: Reference to glossaryItem */`;
      } else if (field.referenceType === 'dialog') {
        // Dialog references - store dialog key as string
        if (Array.isArray(field.referenceData)) {
          const dialogKeys = field.referenceData
            .map(refObj => {
              const dialogKey = refObj.dialog_key || refObj.name || refObj.id;
              return `"${String(dialogKey).replace(/"/g, '\\"')}"`;
            })
            .join(', ');
          console.log(`  ✅ Generated array of dialog keys: new[] { ${dialogKeys} }`);
          return `new[] { ${dialogKeys} }`;
        } else {
          const dialogKey = field.referenceData.dialog_key || field.referenceData.name || field.referenceData.id;
          const stringValue = `"${String(dialogKey).replace(/"/g, '\\"')}"`;
          console.log(`  ✅ Generated dialog key: ${stringValue}`);
          return stringValue;
        }
      }
    }
    
    if (value === null || value === undefined || value === '') {
      if (mappedType.includes('bool')) return 'false';
      if (mappedType.includes('int') || mappedType.includes('float') || mappedType.includes('double')) return '0';
      if (mappedType.includes('[]') || mappedType.includes('Array')) return 'new string[] { }';
      return '""';
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return 'new string[0]';
      }
      const arrItems = value.map(v => formatValueCSharp(v, 'string')).join(', ');
      return `new[] { ${arrItems} }`;
    }

    if (typeof value === 'string') {
      const escaped = value
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t');
      return `"${escaped}"`;
    }

    if (typeof value === 'number') {
      if (mappedType.includes('float')) return `${value}f`;
      // If mapped as int but value has decimals, format as double
      if (mappedType.includes('int') && !Number.isInteger(value)) return value.toString();
      return value.toString();
    }

    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    return `"${String(value)}"`;
  };

  // Start building C# file
  let output = '// Auto-generated ID-based Records Database\n';
  output += '// This file is AUTO-GENERATED and can be safely regenerated.\n';
  output += '// DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.\n\n';
  output += 'using System;\n';
  output += 'using System.Collections.Generic;\n\n';
  output += `namespace ${namespace}\n{\n`;

  // Add IRecord interface
  output += '\tpublic interface IRecord { }\n\n';

  // Generate sealed record classes for each blueprint
  blueprintGroups.forEach(({ blueprint, items: groupItems }) => {
    const blueprintName = applyNamingConvention(blueprint.name, templateConvention);
    const recordClassName = `${blueprintName}Record`;

    output += `\tpublic sealed class ${recordClassName} : IRecord\n\t{\n`;

    // Always include Id field
    output += '\t\tpublic string Id;\n';

    // Collect ALL fields from the blueprint definition (not just fields with values)
    const fieldsMap = new Map<string, { mappedType: string, actualValue: any, isTranslatable: boolean, isReference: boolean, referenceType?: 'types' | 'tags' | 'glossaryItem' | 'dialog' }>();
    const sections = blueprint.sections || [];

    // Process all sections and fields from the blueprint
    sections.forEach((section: any) => {
      const sectionFields = section.fields || [];
      sectionFields.forEach((fieldDef: any) => {
        // Skip if field definition is invalid
        if (!fieldDef || !fieldDef.label) return;
        
        const fieldName = applyNamingConvention(fieldDef.label, fieldConvention);
        const mappedType = template.fieldMappings[fieldDef.type]?.typeName || 'string';

        // Check if this is a reference field
        const isReference = fieldDef.type === 'types' || fieldDef.type === 'tags' || fieldDef.type === 'glossaryItem' || fieldDef.type === 'dialog';
        const referenceType = isReference ? fieldDef.type as ('types' | 'tags' | 'glossaryItem' | 'dialog') : undefined;

        // Use the blueprint's field definition for TYPE generation
        // When single-language mode, suppress translatable to export as plain string
        const isTranslatable = useTranslationKeys ? (fieldDef.trans || fieldDef.translation || false) : false;

        // Find a sample value from any item to help determine array types
        let sampleValue: any = null;
        
        for (const item of groupItems) {
          const fields = item.fields || {};
          const fieldValue = fields[fieldDef.id];
          
          if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
            sampleValue = fieldValue;
            break;
          }
        }

        // Store field definition (include all fields, even if no sample value found)
        if (!fieldsMap.has(fieldName)) {
          fieldsMap.set(fieldName, { mappedType, actualValue: sampleValue, isTranslatable, isReference, referenceType });
        }
      });
    });

    // Generate fields
    fieldsMap.forEach(({ mappedType, actualValue, isTranslatable, isReference, referenceType }, fieldName) => {
      // Create a temporary field object for type detection
      const tempField: Partial<TemplateField> = { isReference, referenceType };
      const csharpType = getCSharpType(mappedType, actualValue, isTranslatable, tempField as TemplateField, blueprints, templateConvention);
      output += `\t\tpublic ${csharpType} ${fieldName};\n`;
    });

    output += '\t}\n\n';
  });

  // Generate Db class with Records dictionary
  output += `\tpublic static class ${className}\n\t{\n`;
  output += '\t\tprivate static readonly Dictionary<string, IRecord> Records = new()\n\t\t{\n';

  // Add all items to the dictionary
  items.forEach((item, index) => {
    const blueprintId = item.blueprint_id;
    if (!blueprintId) return;

    const blueprint = blueprints.find(bp => bp.id === blueprintId);
    if (!blueprint) return;

    const blueprintName = applyNamingConvention(blueprint.name, templateConvention);
    const recordClassName = `${blueprintName}Record`;
    const itemId = generateItemId(item);

    output += `\t\t\t["${itemId}"] = new ${recordClassName}\n\t\t\t{\n`;
    output += `\t\t\t\tId = "${itemId}",\n`;

    // Get fields from item using extractFields to properly handle reference data
    const templateFields = extractFields(item, template, blueprints, blueprintName, lookupContext, useTranslationKeys);

    templateFields.forEach((field) => {
      const exportValue = field.value !== undefined ? field.value : field.defaultValue;
      const isEmpty = exportValue === null || exportValue === undefined || exportValue === '' ||
                     (Array.isArray(exportValue) && exportValue.length === 0);
      
      // Skip empty non-translatable fields
      if (isEmpty && !field.isTranslatable) {
        return;
      }

      const fieldName = applyNamingConvention(field.label, fieldConvention);
      const mappedType = field.mappedType;
      
      let formattedValue: string;
      
      // Check if this is a reference field (types/tags)
      if (field.isReference && field.referenceData && field.referenceType) {
        formattedValue = formatValueCSharp(exportValue, mappedType, field);
      }
      // Check if this field is translatable
      else if (field.string_ids && field.string_ids.length > 0) {
        const stringId = field.string_ids[0];
        const stringItem = stringsMap.get(stringId);
        
        if (stringItem) {
          const keyName = stringItem.abbrev || `key_${stringItem.id}`;
          const safeKeyName = keyName.replace(/[^a-zA-Z0-9_]/g, '_');
          
          if (Array.isArray(exportValue) && field.string_ids.length > 1) {
            const keyRefs = field.string_ids.map((sid, idx) => {
              const sItem = stringsMap.get(sid);
              if (sItem) {
                const kName = sItem.abbrev || `key_${sItem.id}`;
                return `TranslationKeys.${kName.replace(/[^a-zA-Z0-9_]/g, '_')}`;
              }
              return `TranslationKeys.${safeKeyName}_${idx}`;
            }).join(', ');
            formattedValue = `new[] { ${keyRefs} }`;
          } else {
            formattedValue = `TranslationKeys.${safeKeyName}`;
          }
        } else {
          formattedValue = formatValueCSharp(exportValue, mappedType, field);
        }
      } else if (field.isTranslatable && isEmpty) {
        if (Array.isArray(exportValue)) {
          formattedValue = 'new TranslationKey[0]';
        } else {
          formattedValue = 'TranslationKeys.Empty';
        }
      } else {
        formattedValue = formatValueCSharp(exportValue, mappedType, field);
      }

      output += `\t\t\t\t${fieldName} = ${formattedValue},\n`;
    });

    output += '\t\t\t}';
    
    if (index < items.length - 1) {
      output += ',';
    }
    
    output += '\n';
  });

  output += '\t\t};\n\n';

  // Add Get<T> method - unified access for both Records and DialogDatabase
  output += '\t\t/// <summary>\n';
  output += '\t\t/// Unified Get method that can access both Records and Dialog sections.\n';
  output += '\t\t/// For dialog sections, use the format: "dialogKey/sectionId"\n';
  output += '\t\t/// For records, use the ID directly.\n';
  output += '\t\t/// </summary>\n';
  output += '\t\tpublic static T Get<T>(string key) where T : class\n';
  output += '\t\t{\n';
  
  if (dialogs && dialogs.length > 0) {
    output += '\t\t\t// First try DialogDatabase.Dialogs if T is a DialogSection\n';
    output += '\t\t\tif (typeof(T).IsSubclassOf(typeof(DialogSection)) || typeof(T) == typeof(DialogSection))\n\t\t\t{\n';
    output += '\t\t\t\tif (DialogDatabase.Dialogs.TryGetValue(key, out var dialogSection))\n\t\t\t\t{\n';
    output += '\t\t\t\t\treturn dialogSection as T;\n';
    output += '\t\t\t\t}\n';
    output += '\t\t\t\treturn null;\n';
    output += '\t\t\t}\n\n';
  }
  
  output += '\t\t\t// Try Records for IRecord types\n';
  output += '\t\t\tif (typeof(IRecord).IsAssignableFrom(typeof(T)))\n\t\t\t{\n';
  output += '\t\t\t\treturn Records.TryGetValue(key, out var r) ? r as T : null;\n';
  output += '\t\t\t}\n\n';
  output += '\t\t\treturn null;\n';
  output += '\t\t}\n';

  // Add Dialogs section if dialogs are provided
  if (dialogs && dialogs.length > 0) {
    output += '\n';
    output += '\t\tpublic static class Dialogs\n';
    output += '\t\t{\n';
    
    dialogs.forEach(dialog => {
      const dialogIdentifier = applyNamingConvention(dialog.dialog_key || dialog.name, fieldConvention).replace(/[^a-zA-Z0-9_]/g, '_');
      output += `\t\t\tpublic static class ${dialogIdentifier}\n`;
      output += '\t\t\t{\n';
      output += `\t\t\t\tpublic static readonly string Id = "${dialog.id}";\n`;
      output += `\t\t\t\tpublic static readonly string Name = "${(dialog.name || '').replace(/"/g, '\\"')}";\n`;
      output += `\t\t\t\tpublic static readonly string DialogKey = "${dialog.dialog_key || dialog.name}";\n`;
      
      // Render sections
      if (dialog.data && dialog.data.sections && Array.isArray(dialog.data.sections)) {
        dialog.data.sections.forEach((section: any) => {
          const sectionId = applyNamingConvention(section.section_identifier || section.name || 'section', fieldConvention).replace(/[^a-zA-Z0-9_]/g, '_');
          output += `\n\t\t\t\tpublic static class ${sectionId}\n`;
          output += '\t\t\t\t{\n';
          output += `\t\t\t\t\tpublic static readonly string SectionId = "${section.section_identifier || section.name || ''}";\n`;
          
          // Render fields
          if (section.fields && Array.isArray(section.fields)) {
            section.fields.forEach((field: any, fieldIdx: number) => {
              const dialogFieldName = applyNamingConvention(`field_${fieldIdx}_${field.type}`, fieldConvention).replace(/[^a-zA-Z0-9_]/g, '_');
              
              // Field text
              if (field.text) {
                if (field.string_id) {
                  const stringItem = stringsMap.get(field.string_id);
                  if (stringItem) {
                    const keyName = stringItem.abbrev || `key_${stringItem.id}`;
                    const safeKeyName = keyName.replace(/[^a-zA-Z0-9_]/g, '_');
                    output += `\t\t\t\t\tpublic static readonly TranslationKey ${dialogFieldName}_text = TranslationKeys.${safeKeyName};\n`;
                  } else {
                    const escapedText = field.text.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
                    output += `\t\t\t\t\tpublic static readonly string ${dialogFieldName}_text = "${escapedText}";\n`;
                  }
                } else {
                  const escapedText = field.text.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
                  output += `\t\t\t\t\tpublic static readonly string ${dialogFieldName}_text = "${escapedText}";\n`;
                }
              }
              
              output += `\t\t\t\t\tpublic static readonly string ${dialogFieldName}_type = "${field.type}";\n`;
              
              // Options for questions
              if (field.options && Array.isArray(field.options) && field.options.length > 0) {
                output += `\t\t\t\t\tpublic static class ${dialogFieldName}_options\n`;
                output += '\t\t\t\t\t{\n';
                field.options.forEach((option: any, optIdx: number) => {
                  const optionName = applyNamingConvention(`option_${optIdx}`, fieldConvention).replace(/[^a-zA-Z0-9_]/g, '_');
                  if (option.string_id) {
                    const stringItem = stringsMap.get(option.string_id);
                    if (stringItem) {
                      const keyName = stringItem.abbrev || `key_${stringItem.id}`;
                      const safeKeyName = keyName.replace(/[^a-zA-Z0-9_]/g, '_');
                      output += `\t\t\t\t\t\tpublic static readonly TranslationKey ${optionName} = TranslationKeys.${safeKeyName};\n`;
                    } else {
                      const escapedText = (option.option_text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
                      output += `\t\t\t\t\t\tpublic static readonly string ${optionName} = "${escapedText}";\n`;
                    }
                  } else {
                    const escapedText = (option.option_text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
                    output += `\t\t\t\t\t\tpublic static readonly string ${optionName} = "${escapedText}";\n`;
                  }
                });
                output += '\t\t\t\t\t}\n';
              }
            });
          }
          
          output += '\t\t\t\t}\n';
        });
      }
      
      output += '\t\t\t}\n\n';
    });
    
    output += '\t\t}\n';
    console.log(`✅ Rendered ${dialogs.length} dialogs in ID-based Records Database`);
  }

  output += '\t}\n';
  output += '}\n';

  return output;
}
